/**
 * Estado dos campos do AutoForm por modo (um ÚNICO formulário para new, edit e view).
 *
 *   disabledNew  : ids/nomes de campos que continuam VISÍVEIS mas desabilitados no modo `new`;
 *   readOnlyEdit : ids/nomes de campos que continuam VISÍVEIS, carregam o valor e ficam somente leitura no modo `edit`;
 *   view         : todos os campos somente leitura (e o AutoForm não gera botão Salvar).
 *
 * A correspondência é pelo `id`/`name` do campo (ou `fieldIds`), com ou sem prefixo de tabela, e também pelos nomes dos
 * subcampos de componentes compostos (`fields`: geodoc, geography, address...), de modo que listar o nome persistível de um
 * subcampo (`identity_value`) ou o id do composto (`candidate_document`) produz o mesmo efeito.
 */
const text = (value) => String(value ?? '').trim();
const shortName = (value) => text(value).split('.').pop();

const namesOf = (value) => (Array.isArray(value) ? value.flatMap(namesOf) : value === undefined || value === null ? [] : [text(value), shortName(value)].filter(Boolean));

/** Todos os identificadores pelos quais a configuração pode se referir a este campo (inclui subcampos de compostos). */
export const fieldIdentifiers = (field = {}) => {
  const ids = new Set([...namesOf(field.id), ...namesOf(field.name), ...namesOf(field.fieldIds)]);
  if (field.fields && typeof field.fields === 'object') {
    for (const sub of Object.values(field.fields)) for (const name of [...namesOf(sub?.name), ...namesOf(sub?.id)]) ids.add(name);
  }
  return ids;
};

const listed = (list, field) => {
  const wanted = new Set(namesOf(list));
  if (!wanted.size) return false;
  for (const id of fieldIdentifiers(field)) if (wanted.has(id)) return true;
  return false;
};

const apply = (field, flag) => {
  field[flag] = true;
  // Componentes compostos: o estado vale para cada subcampo persistível.
  if (field.fields && typeof field.fields === 'object') for (const sub of Object.values(field.fields)) if (sub && typeof sub === 'object') sub[flag] = true;
};

/**
 * Aplica o estado do modo aos campos já achatados (mutação in-place, como o restante do pipeline do AutoForm).
 * @returns {Array} os mesmos campos
 */
export const applyAutoformFieldModes = (fields = [], { mode = 'edit', disabledNew = [], readOnlyEdit = [] } = {}) => {
  for (const field of fields) {
    if (!field || typeof field !== 'object') continue;
    if (mode === 'view') apply(field, 'readOnly');
    else if (mode === 'new' && listed(disabledNew, field)) { apply(field, 'disabled'); }
    else if (mode === 'edit' && listed(readOnlyEdit, field)) { apply(field, 'readOnly'); }
  }
  return fields;
};
